// Revenue Budget — the Day Zero Budget screen's second view. The bid revenue
// broken out by cost code x cost type (helpers/revenueBudget), shown by
// section beside the Day Zero cost on the same lines. It loads with the Day
// Zero upload (the Revenue column), so there is no import here. The VoltaGrid
// Overview's Bid column reads it; nothing here goes to JDE.
import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { fNum } from '../db/company';
import { selectedJobAtom, selectedVersionAtom, jobUserDataAtom, jobPhaseCodesAtom } from '../store/company';
import { revenueBudgetModel } from '../helpers/revenueBudget';
import { COST_TYPE_DESCRIPTION } from '../helpers/procurementCategories';
import { voltaGridWbsFor, VG_SECTIONS } from '../db/wbsVoltaGrid';
import { isVoltaGridJob } from '../helpers/voltaGrid';

const CT_DESC = {
    '51100': 'Regular Time', '51200': 'Overtime', '51300': 'Burden', '51400': 'BYOD', '51500': 'Per Diem',
    '51600': 'Truck Pay', '51700': 'Welding Rig Pay', '52100': 'Inside Equipment Rental',
    ...COST_TYPE_DESCRIPTION,
};

const C = { navy: '#0F2A4A', blue: '#2563eb', blueSoft: '#eff6ff', border: '#e2e8f0', text: '#0f172a', body: '#334155', muted: '#64748b', warn: '#b45309', warnSoft: '#fffbeb', err: '#dc2626', errSoft: '#fef2f2', zebra: '#f9fafb' };
const money = (n) => (n ? '$' + fNum(n, 1) : '-');
const ratioText = (rev, cost) => (rev && cost ? (rev / cost).toFixed(3) + '×' : '-');

function RevenueBudget() {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [open, setOpen] = useState({});

    const isVg = isVoltaGridJob(selectedJob);
    // Sections as the Overview's budget grid has them: the Productivity
    // Tracker discipline, else (VoltaGrid) the code's WBS section. Tracker
    // disciplines named for a WBS section sort in section order.
    const model = useMemo(() => {
        const tracker = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab !== 'Productivity Tracker' || d?.Forecast_Field_Name !== 'discipline' || !d?.Forecast_Value) return;
            if (d.Version_GUID != null && String(d.Version_GUID) !== String(selectedVersion)) return;
            tracker[String(d.Forecast_Row_ID).trim()] = String(d.Forecast_Value).trim();
        });
        const orderOf = (name) => (VG_SECTIONS.find((s) => s.name.toLowerCase() === name.toLowerCase()) || { no: 50 }).no;
        const sectionOf = (cc) => {
            const name = tracker[cc] || (isVg ? voltaGridWbsFor(cc)?.name : null);
            return name ? { name, order: orderOf(name) } : { name: 'Unassigned', order: 999 };
        };
        return revenueBudgetModel({ userData: jobUserData, version: selectedVersion, sectionOf });
    }, [jobUserData, selectedVersion, isVg]);
    const tie = model.revenue - model.dayZeroRevenue;

    const th ={ padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#1e293b', textAlign: 'right', borderBottom: `2px solid ${C.blue}`, background: '#f8fafc', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left' };
    const td = { padding: '6px 10px', fontSize: 13, color: C.body, textAlign: 'right', borderBottom: `1px solid ${C.border}`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
    const tdL = { ...td, textAlign: 'left' };
    const kpi = (label, value, sub, tone) => (
        <div key={label} style={{ flex: '1 1 180px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: tone || C.text, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
    );

    return (
        <div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                {kpi('Revenue Budget', money(model.revenue), `${model.sections.reduce((a, s) => a + s.lines.filter((l) => l.revenue).length, 0)} lines`)}
                {kpi('Day Zero Cost', money(model.cost), 'cost types 5xxxx')}
                {kpi('Bill-to-Cost', ratioText(model.revenue, model.cost), 'revenue ÷ cost')}
                {kpi('Day Zero Revenue Line', money(model.dayZeroRevenue),
                    model.has && model.dayZeroRevenue ? (Math.abs(tie) < 1 ? 'ties to the breakout' : `breakout is ${tie > 0 ? 'over' : 'under'} by $${fNum(Math.abs(tie), 1)}`) : '4xxxx on the cost budget',
                    model.has && model.dayZeroRevenue && Math.abs(tie) >= 1 ? C.warn : null)}
            </div>

            {!model.sections.length ? (
                <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
                    No Day Zero Budget or Revenue Budget on this version yet.
                </div>
            ) : (
                <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={thL}>Section / Cost Code</th>
                                <th style={thL}>Cost Type</th>
                                <th style={th}>Revenue</th>
                                <th style={th}>Hours</th>
                                <th style={th}>Day Zero Cost</th>
                                <th style={th}>Bill-to-Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {model.sections.map((s) => {
                                const isOpen = !!open[s.name];
                                return (
                                    <React.Fragment key={s.name}>
                                        <tr style={{ background: C.blueSoft, cursor: 'pointer' }} onClick={() => setOpen((p) => ({ ...p, [s.name]: !p[s.name] }))}>
                                            <td style={{ ...tdL, fontWeight: 700, color: C.navy }} colSpan={2}>{isOpen ? '▾' : '▸'} {s.name}</td>
                                            <td style={{ ...td, fontWeight: 700, color: C.navy }}>{money(s.revenue)}</td>
                                            <td style={td} />
                                            <td style={{ ...td, fontWeight: 700, color: C.navy }}>{money(s.cost)}</td>
                                            <td style={{ ...td, fontWeight: 700, color: C.navy }}>{ratioText(s.revenue, s.cost)}</td>
                                        </tr>
                                        {isOpen && s.lines.map((l, i) => (
                                            <tr key={`${l.cc}|${l.ct}`} style={{ background: i % 2 ? C.zebra : '#fff' }}>
                                                <td style={{ ...tdL, paddingLeft: 28 }}>{l.cc} <span style={{ color: C.muted }}>{phaseCodes?.[l.cc] || ''}</span></td>
                                                <td style={tdL}>{l.ct} <span style={{ color: C.muted }}>{CT_DESC[l.ct] || ''}</span></td>
                                                <td style={{ ...td, color: l.revenue ? C.text : C.warn, fontWeight: 500 }}>{l.revenue ? money(l.revenue) : 'unpriced'}</td>
                                                <td style={td}>{l.hours ? fNum(l.hours, 1) : '-'}</td>
                                                <td style={td}>{money(l.cost)}</td>
                                                <td style={td}>{ratioText(l.revenue, l.cost)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                            <tr style={{ background: '#eff6ff', borderTop: `2px solid ${C.blue}` }}>
                                <td style={{ ...tdL, fontWeight: 700, color: C.navy }} colSpan={2}>Total</td>
                                <td style={{ ...td, fontWeight: 700, color: C.navy }}>{money(model.revenue)}</td>
                                <td style={td} />
                                <td style={{ ...td, fontWeight: 700, color: C.navy }}>{money(model.cost)}</td>
                                <td style={{ ...td, fontWeight: 700, color: C.navy }}>{ratioText(model.revenue, model.cost)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default RevenueBudget;
